import { ChangeEvent, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useDebounce } from '@uidotdev/usehooks';
import { Board } from '~/components/Shared/Layout/Board/Board';
import { ErrorMessage } from '~/components/Shared/ErrorMessage/ErrorMessage';
import { Button, InputBar, Pagination, Spinner } from '~/ui';
import '~/assets/css/select-table.css';
import styles from './/FoodBoard.module.scss';
import { TableSelect } from '~/ui/TableSelect/TableSelect';
import { TableAmountPage } from '~/ui/TableAmountPage/TableAmountPage';
import { FoodTable } from './FoodTable/FoodTable';
import { useLocalStorage } from '~/utils/useLocalStorage';
import { useTranslation } from 'react-i18next';
import { useToastError } from '~/utils/useToastError';
import { useToastSuccess } from '~/utils/useToastSuccess';
import { getPaginate } from '~/utils/getPaginate';

interface SelectedPage {
  selected: number;
}

interface Points {
  page: number;
}

export const FoodBoard = () => {
  const { initialValues, storedInitialValues, create } =
    useLocalStorage<Points>({
      initialValues: { page: 15 },
      key: 'food-points',
      exclude: [],
    });
  const [sort, setSort] = useState({
    field: '',
    direction: '',
  });
  const [perPage, setPerPage] = useState(
    storedInitialValues?.page || initialValues.page
  );
  const { paginate } = getPaginate();
  const [page, setPage] = useState<number>(paginate || 1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const toastError = useToastError();
  const toastSuccess = useToastSuccess();
  const client = useQueryClient();
  const { t } = useTranslation();

  useEffect(() => {
    create({ page: perPage });
  }, [perPage]);

  const params = {
    sort: sort.direction,
    perPage,
    field: sort.field,
    query: debouncedSearchTerm,
    page,
  };

  const { data, isError, error } = useQuery({
    queryFn: async () => {
      const response = await instance.get('food-admin', { params });
      return response.data;
    },
    queryKey: ['food', params],
    keepPreviousData: true,
  });

  let content: React.ReactNode;

  const handleSelect = (id: number) => {
    if (selectedItems.includes(id)) {
      const filteredFoods = selectedItems.filter(
        selectedId => selectedId !== id
      );
      setSelectedItems(filteredFoods);
    } else {
      setSelectedItems([...selectedItems, id]);
    }
  };

  const handleToggleAll = () => {
    if (selectedItems.length === data.data?.data.length) {
      setSelectedItems([]);
    } else {
      const foodIdArray = data.data?.data.map(
        (accomodation: any) => accomodation.id
      );
      setSelectedItems(foodIdArray);
    }
  };

  const handleDeleteSelected = async () => {
    try {
      await instance.delete('admin/foods-many', {
        data: { food_ids: selectedItems },
      });
      setSelectedItems([]);
      client.invalidateQueries(['food']);
      toastSuccess(t('food_point_removed'));
    } catch (error) {
      toastError(error);
    }
  };

  if (data) {
    content = (
      <FoodTable
        selectedItems={selectedItems}
        handleToggle={handleSelect}
        handleToggleAll={handleToggleAll}
        foods={data.data}
        sort={sort}
        setSort={setSort}
      />
    );
  } else if (isError) {
    content = <ErrorMessage error={error} />;
  } else {
    content = <Spinner />;
  }

  useEffect(() => {
    setPage(paginate);
  }, [paginate]);

  return (
    <Board>
      <div className={styles.foodTableOptions}>
        <TableSelect perPage={perPage} setPerPage={setPerPage} />
        <div className={styles.tableHeadRightWrapper}>
          {!!selectedItems.length && (
            <Button
              text={t('delete')}
              color="red"
              onClick={handleDeleteSelected}
              className={styles.deleteButton}
            />
          )}
          <InputBar
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            value={searchTerm}
            placeholder={t('search_by_name')}
            classNameInput={styles.foodTableInput}
            label={`${t('search')}:`}
            star={false}
            classNameInputWrapper={styles.foodTableInputWrapper}
          />
        </div>
      </div>
      {content}
      <div className={styles.foodTablePagination}>
        <TableAmountPage
          firstRow={data?.from}
          lastRow={data?.to}
          total={data?.total}
        />
        <Pagination
          forcePage={paginate}
          pageCount={data?.last_page}
          onPageChange={(selectedPage: SelectedPage) => {
            setPage(selectedPage.selected + 1);
          }}
        />
      </div>
    </Board>
  );
};
